import { useEffect } from 'react';

import config from '@/config';
import { env } from '@/env';
import { useIsAdmin, useUser } from '@/modules/auth/hooks';
import type { AuthUser } from '@/modules/auth/schemas/oauth';
import { useSubscriptionType } from '@/modules/settings/hooks';

import type { ChatVisitorProps } from '../types';

type InstanceType = 'enterprise' | 'workspace' | 'demo';

const INSTANCE_TYPE: InstanceType = env.VITE_DEMO ? 'demo' : env.VITE_SAAS ? 'workspace' : 'enterprise';
const SCRIPT_ID = 'social-intents-chat-script';
const SCRIPT_SRC = 'https://www.socialintents.com/api/chat/socialintents.1.4.js';

const getChatVisitorProps = (user: AuthUser | null): ChatVisitorProps => {
    if (config.demo.enable && user?.decodedIdToken) {
        const { name, email } = user.decodedIdToken;

        return { name, email };
    }

    return user ? { name: user.name, email: user.email, userId: String(user.id) } : {};
};

function appIsLoaded() {
    return new Promise(resolve => {
        if (document.readyState === 'complete') {
            resolve(true);
        } else {
            window.addEventListener('load', () => resolve(true));
        }
    });
}

async function appIsReady() {
    await appIsLoaded();

    return new Promise(resolve => {
        if ('requestIdleCallback' in globalThis) {
            requestIdleCallback(() => resolve(true));
        } else {
            requestAnimationFrame(() => resolve(true));
        }
    });
}

export const useChat = () => {
    const user = useUser();
    const isUserAdmin = useIsAdmin();
    const subscription = useSubscriptionType();

    useEffect(() => {
        if (document.getElementById(SCRIPT_ID)) return;

        (async () => {
            await appIsReady();

            // Re-check after the async wait so a concurrent mount (e.g. React strict-mode) can't append a duplicate tag.
            if (document.getElementById(SCRIPT_ID)) return;

            const script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.async = true;
            script.src = `${SCRIPT_SRC}#${config.socialIntentsAppId}`;
            document.body.appendChild(script);
        })();
    }, []);

    useEffect(() => {
        const { name = '', email = '', userId = '' } = getChatVisitorProps(user);

        const applyVisitorData = () => {
            window.SI_API?.setChatInfo?.(name, email, '', '', '');
            window.SI_API?.addParams?.([
                // Social Intents filters out custom params named `name` and `email` (they collide with built-in visitor fields), so they are namespaced under workspace_*.
                { name: 'workspace_name', value: name },
                { name: 'workspace_email', value: email },
                { name: 'user_id', value: userId },
                { name: 'instance_type', value: INSTANCE_TYPE },
                { name: 'subscription', value: subscription ?? '' },
                { name: 'is_admin', value: String(isUserAdmin) },
            ]);
        };

        if (window.SI_API) {
            applyVisitorData();
        } else {
            // Social Intents calls onSIApiReady once SI_API is initialised. Re-assigning on each effect run lets the latest user data win if the user changes before the SDK is ready.
            window.onSIApiReady = applyVisitorData;
        }
    }, [user, subscription, isUserAdmin]);

    const show = () => {
        window.SI_API?.showPopup?.();
    };

    const close = () => {
        window.SI_API?.hidePopup?.();
    };

    return { show, close };
};
